import type { ProductWidget as ProductWidgetType } from '../../types/coach';
import { Badge, Card } from '../atoms';

interface ProductWidgetProps {
  widget: ProductWidgetType;
}

export function ProductWidget({ widget }: ProductWidgetProps) {
  const { product } = widget;

  // Defensive programming - should not happen with validation but just in case
  if (!product) {
    return (
      <Card variant="glass" className="max-w-sm p-4">
        <p className="text-sm text-text-muted">Invalid product widget</p>
      </Card>
    );
  }

  const { name, description, imageUrl, price, id } = product;

  return (
    <Card variant="glass" className="max-w-lg overflow-hidden">
      <div className="flex p-3 space-x-3">
        {/* Product Image */}
        {imageUrl && (
          <div className="flex-shrink-0">
            <div className="w-20 h-20 overflow-hidden rounded-lg bg-surface-subtle">
              <img 
                src={imageUrl} 
                alt={name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Hide image if it fails to load
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          </div>
        )}
        
        {/* Product Content */}
        <div className="flex-1 min-w-0">
          {/* Product Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-text-primary leading-tight">
                {name}
              </h3>
              <p className="text-xs text-text-muted mt-0.5">
                ID: {id}
              </p>
            </div>
            <div className="flex flex-wrap gap-1 ml-2">
              {price?.qv && (
                <Badge variant="info" size="xs">
                  {price.qv} QV
                </Badge>
              )}
              {price && (
                <Badge 
                  variant={price.inStock ? "success" : "warning"} 
                  size="xs"
                >
                  {price.inStock ? "In Stock" : "Out of Stock"}
                </Badge>
              )}
            </div>
          </div>

          {/* Product Description */}
          {description && (
            <div className="mb-2">
              <p className="text-xs text-text-secondary leading-relaxed line-clamp-2">
                {description}
              </p>
            </div>
          )}

          {/* Price Information */}
          {price && (
            <div className="flex items-center justify-between pt-2 border-t border-border-subtle">
              {price.commission && (
                <div className="text-xs">
                  <span className="text-text-muted">Price: </span>
                  <span className="font-semibold text-success">
                    {price.currency} {price.commission}
                  </span>
                </div>
              )}
              {price.tax && (
                <div className="text-xs text-text-muted">
                  Tax: {price.tax}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}